import { useAnalytics } from '@contexts/AnalyticsContext'
import {
  FetchMembershipAnalyticsStatisticsParams,
  MembershipKinds,
} from '@models/membership/MembershipAnalyticsStatistics'
import { fetchAnalyticsStatistics } from '@services/AnalyticsStatistics'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'

/**
 * Statistics for the Revenue page. `followsPeriod: false` is a "Today" block:
 * it ignores the date range (the endpoint still wants a window, so it gets
 * today) and only refetches when the program changes.
 */
const useRevenueStatistics = <T>(
  kind: MembershipKinds,
  followsPeriod: boolean
) => {
  const {
    selectedDates,
    selectedMembershipPrograms,
    isMembershipProgramReady,
  } = useAnalytics()

  const [data, setData] = useState<T>()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string>()
  // Only the newest request may write: filters change faster than responses.
  const latest = useRef(0)

  const start = followsPeriod ? selectedDates?.[0] : undefined
  const end = followsPeriod ? selectedDates?.[1] : undefined

  useEffect(() => {
    if (!isMembershipProgramReady) return
    if (followsPeriod && (!start || !end)) return

    const today = dayjs().format('YYYY-MM-DD')
    const params: FetchMembershipAnalyticsStatisticsParams = {
      entity: 'membership',
      kind,
      startDate: start ?? today,
      endDate: end ?? today,
      programIds: selectedMembershipPrograms,
    }

    const requestId = ++latest.current
    setIsLoading(true)
    setError(undefined)

    fetchAnalyticsStatistics<T>(params)
      .then(({ data: response }) => {
        if (requestId !== latest.current) return
        if (!response) {
          setData(undefined)
          setError('No data available.')
          return
        }
        setData(response)
      })
      .catch((e) => {
        if (requestId !== latest.current) return
        console.error('Error fetching membership statistics:', e)
        setData(undefined)
        setError('Failed to fetch data.')
      })
      .finally(() => {
        if (requestId === latest.current) setIsLoading(false)
      })
  }, [
    kind,
    followsPeriod,
    start,
    end,
    selectedMembershipPrograms,
    isMembershipProgramReady,
  ])

  return { data, isLoading, error }
}

export default useRevenueStatistics
